import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Account, Booking } from '../types';
import { cancelBooking, deleteAccount, getFavorites, getMyAccounts, getMyBookings, ApiError } from '../lib/api';
import { useSellWizardStore } from '../store/sellWizardStore';
import { useToastStore } from '../store/toastStore';
import AccountCard from '../components/AccountCard';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import PriceTag from '../components/PriceTag';
import CollectorTierBadge from '../components/CollectorTierBadge';
import { hapticImpact, hapticNotification } from '../lib/telegram';

const STATUS_INFO: Record<string, { label: string; className: string }> = {
  PENDING: { label: '🕓 На модерации', className: 'bg-amber-700/40 text-amber-200' },
  ACTIVE: { label: '✅ Активно', className: 'bg-emerald-700/40 text-emerald-200' },
  RESERVED: { label: '🔒 Забронировано', className: 'bg-sky-700/40 text-sky-200' },
  SOLD: { label: '💰 Продано', className: 'bg-zinc-700/40 text-zinc-300' },
  REJECTED: { label: '❌ Отклонено', className: 'bg-red-700/40 text-red-200' },
};

type Tab = 'listings' | 'bookings' | 'favorites';

export default function ProfilePage() {
  const [tab, setTab] = useState<Tab>('listings');
  const [listings, setListings] = useState<Account[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [favorites, setFavorites] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { loadFromAccount } = useSellWizardStore();
  const showToast = useToastStore((s) => s.show);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, b, f] = await Promise.all([getMyAccounts(), getMyBookings(), getFavorites()]);
      setListings(l);
      setBookings(b);
      setFavorites(f);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const doDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteAccount(confirmDeleteId);
      hapticNotification('success');
      setListings((l) => l.filter((a) => a.id !== confirmDeleteId));
    } catch (e) {
      hapticNotification('error');
      showToast(e instanceof ApiError ? e.message : 'Не удалось удалить', 'error');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const doCancelBooking = async () => {
    if (!confirmCancelId) return;
    try {
      await cancelBooking(confirmCancelId);
      hapticNotification('success');
      setBookings((b) => b.filter((x) => x.id !== confirmCancelId));
    } catch (e) {
      hapticNotification('error');
      showToast(e instanceof ApiError ? e.message : 'Не удалось отменить бронь', 'error');
    } finally {
      setConfirmCancelId(null);
    }
  };

  const editListing = (account: Account) => {
    hapticImpact('light');
    loadFromAccount(account);
    navigate('/sell');
  };

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-30 flex border-b border-mlbb-border bg-mlbb-bg/95 backdrop-blur-sm">
        {(
          [
            ['listings', 'Мои объявления'],
            ['bookings', 'Мои брони'],
            ['favorites', 'Избранное'],
          ] as [Tab, string][]
        ).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-3 text-sm font-medium ${
              tab === t ? 'border-b-2 border-mlbb-accent-from text-white' : 'text-white/40'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {loading ? (
          <div className="py-10 text-center text-white/40">Загрузка...</div>
        ) : tab === 'listings' ? (
          listings.length === 0 ? (
            <EmptyState icon="📦" title="Пока нет объявлений" actionLabel="Продать аккаунт" onAction={() => navigate('/sell')} />
          ) : (
            <div className="space-y-3">
              {listings.map((a) => {
                const info = STATUS_INFO[a.status];
                return (
                  <div key={a.id} className="rounded-2xl border border-mlbb-border bg-mlbb-card p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <PriceTag price={a.price} currency={a.currency} />
                      {info && (
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${info.className}`}>
                          {info.label}
                        </span>
                      )}
                    </div>
                    {a.gameId && (
                      <div className="text-xs text-white/50">
                        🎮 Game ID: {a.gameId} ({a.serverId})
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-xs text-white/50">
                      <span>{a.server}</span>
                      <CollectorTierBadge tierId={a.collectorTier} level={a.collectorTierLevel} />
                    </div>
                    {a.status === 'REJECTED' && a.rejectReason && (
                      <div className="mt-2 rounded-lg bg-red-900/20 p-2 text-xs text-red-200">
                        Причина: {a.rejectReason}
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      {['PENDING', 'ACTIVE', 'REJECTED'].includes(a.status) && (
                        <button
                          onClick={() => editListing(a)}
                          className="flex-1 rounded-full bg-mlbb-accent py-2 text-xs font-medium text-white"
                        >
                          Редактировать
                        </button>
                      )}
                      {['PENDING', 'ACTIVE', 'REJECTED'].includes(a.status) && (
                        <button
                          onClick={() => setConfirmDeleteId(a.id)}
                          className="flex-1 rounded-full border border-mlbb-border py-2 text-xs text-white/70"
                        >
                          Снять с продажи
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : tab === 'bookings' ? (
          bookings.length === 0 ? (
            <EmptyState icon="🔖" title="Нет активных броней" />
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} className="rounded-2xl border border-mlbb-border bg-mlbb-card p-3">
                  <AccountCard account={b.account} />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-white/50">Статус брони: {b.status}</span>
                    {b.status === 'PENDING' && (
                      <button
                        onClick={() => setConfirmCancelId(b.id)}
                        className="rounded-full border border-mlbb-border px-3 py-1.5 text-xs text-white/70"
                      >
                        Отменить
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : favorites.length === 0 ? (
          <EmptyState icon="🤍" title="Пока пусто" subtitle="Добавляй аккаунты в избранное из каталога" />
        ) : (
          <div className="space-y-3">
            {favorites.map((a) => (
              <AccountCard key={a.id} account={a} />
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmDeleteId}
        title="Снять объявление с продажи?"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
      <ConfirmModal
        open={!!confirmCancelId}
        title="Отменить бронь?"
        danger
        onConfirm={doCancelBooking}
        onCancel={() => setConfirmCancelId(null)}
      />
    </div>
  );
}
